import React from 'react';
import { Button, Dropdown, Modal, Space, message } from 'antd';
import type { MenuProps } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
    CANCEL_WEBSITE_CONTENT_SCHEDULE,
    CREATE_WEBSITE_CONTENT_PREVIEW_LINK,
    DELETE_WEBSITE_CONTENT,
    PUBLISH_WEBSITE_CONTENT,
    TAKE_WEBSITE_CONTENT_OFFLINE,
} from '../graphql/mutations';
import { WEBSITE_CONTENT_LANGS, WebsiteAdminListItem, WebsiteContentLang, WebsitePreviewLink } from '../graphql/types';
import { copyText, openPreviewWindow } from '../utils/actions';
import { extractMissingFields, resolveWebsiteContentError } from '../utils/errorMessage';
import { localizedPublicUrl } from '../utils/format';

interface ContentRowActionsProps {
    item: WebsiteAdminListItem;
    onChanged: () => void;
}

/** 列表行操作：编辑、预览、复制链接常驻；发布、取消定时、下架、删除按状态显示 */
const ContentRowActions: React.FC<ContentRowActionsProps> = ({ item, onChanged }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { content } = item;
    const variables = { id: content.id };
    const [publish] = useMutation(PUBLISH_WEBSITE_CONTENT);
    const [cancelSchedule] = useMutation(CANCEL_WEBSITE_CONTENT_SCHEDULE);
    const [takeOffline] = useMutation(TAKE_WEBSITE_CONTENT_OFFLINE);
    const [remove] = useMutation(DELETE_WEBSITE_CONTENT);
    const [createPreviewLink] = useMutation<{ createWebsiteContentPreviewLink: WebsitePreviewLink }>(
        CREATE_WEBSITE_CONTENT_PREVIEW_LINK,
    );

    const editPath = `/website-content/list/${content.id}/edit`;

    const showError = (e: unknown) => {
        const text = resolveWebsiteContentError(e, t);
        if (text) message.error(text);
    };

    const run = async (action: () => Promise<unknown>, successKey: string) => {
        try {
            await action();
            message.success(t(successKey));
            onChanged();
        } catch (e) {
            if (extractMissingFields(e).length > 0) {
                // 列表不显示各语言字段，缺项时引导到编辑页定位标红
                Modal.confirm({
                    title: t('websiteContent.list.incompleteTitle'),
                    content: resolveWebsiteContentError(e, t),
                    okText: t('websiteContent.list.goEdit'),
                    onOk: () => navigate(editPath),
                });
                return;
            }
            showError(e);
        }
    };

    const confirm = (titleKey: string, contentKey: string | null, onOk: () => Promise<void>, danger = false) => {
        Modal.confirm({
            title: t(titleKey, { title: item.title || t('websiteContent.list.untitled') }),
            content: contentKey ? t(contentKey) : undefined,
            okButtonProps: { danger },
            onOk,
        });
    };

    const handlePreview = (lang: WebsiteContentLang) => {
        openPreviewWindow(async () => {
            const { data } = await createPreviewLink({ variables: { id: content.id, lang } });
            return data?.createWebsiteContentPreviewLink.url ?? null;
        }).catch(showError);
    };

    const doCopy = (lang: WebsiteContentLang) => {
        copyText(localizedPublicUrl(content.publicUrl, lang))
            .then(() => message.success(t('websiteContent.list.linkCopied')))
            .catch(() => message.error(t('websiteContent.list.copyFailed')));
    };

    const handleCopy = (lang: WebsiteContentLang) => {
        if (content.status === 'PUBLISHED') {
            doCopy(lang);
            return;
        }
        // 未发布的链接官网打不开，提前分享会被社交平台缓存成 404
        Modal.confirm({
            title: t('websiteContent.list.copyUnpublishedTitle'),
            content: t('websiteContent.list.copyUnpublishedContent'),
            okText: t('websiteContent.list.copyAnyway'),
            onOk: () => doCopy(lang),
        });
    };

    const langMenu = (onClick: (lang: WebsiteContentLang) => void): MenuProps => ({
        items: WEBSITE_CONTENT_LANGS.map((lang) => ({ key: lang, label: t(`websiteContent.lang.${lang}`) })),
        onClick: ({ key }) => onClick(key as WebsiteContentLang),
    });

    const statusItems: MenuProps['items'] = [];
    const canPublish = content.status === 'DRAFT' || content.status === 'OFFLINE'
        || (content.status === 'PUBLISHED' && content.hasUnpublishedChanges);
    if (canPublish) {
        const key = content.status === 'PUBLISHED' ? 'update' : content.status === 'OFFLINE' ? 'republish' : 'publish';
        statusItems.push({
            key: 'publish',
            label: t(`websiteContent.actions.${key}`),
            onClick: () => confirm(`websiteContent.list.confirm.${key}`, null, () =>
                run(() => publish({ variables: { ...variables, input: {} } }), `websiteContent.list.done.${key}`)),
        });
    }
    if (content.status === 'SCHEDULED') {
        statusItems.push({
            key: 'cancelSchedule',
            label: t('websiteContent.actions.cancelSchedule'),
            onClick: () => confirm('websiteContent.list.confirm.cancelSchedule', null, () =>
                run(() => cancelSchedule({ variables }), 'websiteContent.list.done.cancelSchedule')),
        });
    }
    if (content.status === 'PUBLISHED') {
        statusItems.push({
            key: 'offline',
            label: t('websiteContent.actions.takeOffline'),
            onClick: () => confirm('websiteContent.list.confirm.takeOffline', 'websiteContent.list.confirm.takeOfflineHint',
                () => run(() => takeOffline({ variables }), 'websiteContent.list.done.takeOffline')),
        });
    }
    if (content.status === 'DRAFT' || content.status === 'OFFLINE') {
        statusItems.push({
            key: 'delete',
            danger: true,
            label: t('websiteContent.actions.delete'),
            onClick: () => confirm('websiteContent.list.confirm.delete', 'websiteContent.list.confirm.deleteHint',
                () => run(() => remove({ variables }), 'websiteContent.list.done.delete'), true),
        });
    }

    return (
        <Space size={0} wrap>
            <Button type="link" size="small" className="!px-1" onClick={() => navigate(editPath)}>
                {t('websiteContent.actions.edit')}
            </Button>
            <Dropdown menu={langMenu(handlePreview)}>
                <Button type="link" size="small" className="!px-1">
                    {t('websiteContent.actions.preview')} <DownOutlined />
                </Button>
            </Dropdown>
            <Dropdown menu={langMenu(handleCopy)}>
                <Button type="link" size="small" className="!px-1">
                    {t('websiteContent.actions.copyLink')} <DownOutlined />
                </Button>
            </Dropdown>
            {statusItems.length > 0 && (
                <Dropdown menu={{ items: statusItems }}>
                    <Button type="link" size="small" className="!px-1">
                        {t('websiteContent.actions.more')} <DownOutlined />
                    </Button>
                </Dropdown>
            )}
        </Space>
    );
};

export default ContentRowActions;
